import Vue from "vue";
import Vuex from "vuex";

import basicInfo from "./vuex-modules/basic-info";
import attackInfo from "./vuex-modules/attack";
import damageInfo from "./vuex-modules/damage";
import customInfo from "./vuex-modules/custom";
import monkInfo from "./vuex-modules/monk";
import psionInfo from "./vuex-modules/psion";

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    basic: basicInfo,
    attack: attackInfo,
    damage: damageInfo,
    custom: customInfo,
    monk: monkInfo,
    psion: psionInfo,
  },
  state: {
    basicTagMapping: {
      name: "basic/setName",
      emote: "basic/setEmote",
      class: "basic/setPowerClass",
      level: "basic/setLevel",
      keywords: "basic/setKeywords",
      hiteffect: "attack/setHitEffect",
      action: "basic/setActionType",
      range: "basic/setRange",
      type: "basic/setTypeName",
      target: "basic/setTarget",
      trigger: "basic/setTrigger",
      beforespecial: "basic/setBeforeSpecial",
      beforeeffect: "basic/setBeforeEffect",
      effect: "basic/setEffect",
      special: "basic/setSpecial",
      requirement: "basic/setRequirement",
      secondarytarget: "attack/setSecondaryTarget",
      tertiarytarget: "attack/setTertiaryTarget",
      firstfailedsave: "attack/setFirstFailedSave",
      secondfailedsave: "attack/setSecondFailedSave",
      thirdfailedsave: "attack/setThirdFailedSave",
      attacktechnique: "monk/setAttackTechniqueName",
      movetechnique: "monk/setMoveTechniqueName",
      mttype: "monk/setMoveTechniqueType",
      mtrange: "monk/setMoveTechniqueRange",
      mttarget: "monk/setMoveTechniqueTarget",
      mteffect: "monk/setMoveTechniqueEffect",
    },
  },
  mutations: {},
  getters: {},
  actions: {
    setFromParsedTags: ({ commit, state, dispatch, rootState }, tags) => {
      let highestMultiAttack = 0;

      tags.forEach((tag) => {
        const parts = tag.split("=");

        if (parts.length == 2) {
          const key = parts[0];
          const value = parts[1].replace(" ♦ ", "");

          if (key in state.basicTagMapping) {
            commit(state.basicTagMapping[key], value);
          } else if (
            [
              "atwill",
              "encounter",
              "daily",
              "item",
              "other",
              "ability",
              "skill",
            ].includes(key)
          ) {
            commit("basic/setType", key);
          } else if (key == "attack") {
            commit("attack/setIsAttack", true);

            dispatch("parseAttack", {
              value,
              key: "primary",
            });
          } else if (key == "multiattacktoggle") {
            commit("attack/setType", "area");
          } else if (key.includes("multiattack")) {
            const keyWithoutMulti = key.replace("multiattack", "");
            const attackNo = parseInt(keyWithoutMulti);

            if (!isNaN(attackNo) && attackNo > highestMultiAttack) {
              highestMultiAttack = attackNo;
            }
          } else if (key == "secondaryattack") {
            commit("attack/setType", "multiple");

            dispatch("parseAttack", {
              value,
              key: "secondary",
            });
          } else if (key == "tertiaryattack") {
            commit("attack/setHasTertiary", true);

            dispatch("parseAttack", {
              value,
              key: "tertiary",
            });
          } else if (key == "damage") {
            dispatch("parseDamage", {
              value,
              key: "primary",
            });
          } else if (key == "secondarydamage") {
            dispatch("parseDamage", {
              value,
              key: "secondary",
            });
          } else if (key == "tertiarydamage") {
            dispatch("parseDamage", {
              value,
              key: "tertiary",
            });
          } else if (!key.includes("critical")) {
            const custom = rootState.custom.custom;
            const newIndex = custom.length;

            commit("custom/addBlankCustomTag");
            commit("custom/setCustomTagLabel", {
              value: key.replaceAll("**", "").replace(":", ""),
              index: newIndex,
            });
            commit("custom/setCustomTagValue", {
              value,
              index: newIndex,
            });
          }
        }
      });

      if (highestMultiAttack != 0) {
        commit("attack/setMaxTargets", `${highestMultiAttack}`);
      }
    },
    parseAttack: ({ commit }, payload) => {
      const mappings = {
        primary: {
          stat: "attack/setStat",
          defense: "attack/setDefense",
          staticBonus: "attack/setStaticBonus",
          weaponNo: "attack/setWeaponNumber",
          bonusSource: "attack/setBonusSource",
          critRange: "attack/setCritRange",
        },
        secondary: {
          stat: "attack/setSecondaryStat",
          defense: "attack/setSecondaryDefense",
          staticBonus: "attack/setSecondaryStaticBonus",
          weaponNo: "attack/setSecondaryWeaponNumber",
          bonusSource: "attack/setSecondaryBonusSource",
          critRange: "attack/setSecondaryCritRange",
        },
        tertiary: {
          stat: "attack/setTertiaryStat",
          defense: "attack/setTertiaryDefense",
          staticBonus: "attack/setTertiaryStaticBonus",
          weaponNo: "attack/setTertiaryWeaponNumber",
          bonusSource: "attack/setTertiaryBonusSource",
          critRange: "attack/setTertiaryCritRange",
        },
      };

      const thisMapping =
        payload.key in mappings ? mappings[payload.key] : false;
      const value = payload.value;

      if (thisMapping && value) {
        const dieRollWithCrit = value.match(/1d20cs>[\d]+/);

        if (dieRollWithCrit) {
          const dieRollParts = dieRollWithCrit[0].split(">");
          const num = dieRollParts[1];
          commit(thisMapping.critRange, parseInt(num));
        }

        const modifierArr = value.match(/@{(.*?)-mod}/);

        if (modifierArr && modifierArr.length) {
          const mod = modifierArr[1];
          const modUpper = mod.charAt(0).toUpperCase() + mod.slice(1);
          commit(thisMapping.stat, modUpper);
        }

        const defenseArr = value.match(/\*\*(.*)\*\*/);

        if (defenseArr && defenseArr.length) {
          const def = defenseArr[1];
          commit(thisMapping.defense, def);
        }

        const weaponArr = value.match(/@{weapon-(.*)-attack}/);

        if (weaponArr && weaponArr.length) {
          const num = weaponArr[1];
          commit(thisMapping.weaponNo, num);
        }

        const staticBonusArr = value.match(/\[\[ (\d*) \]\] \[static bonus\]/);

        if (staticBonusArr && staticBonusArr.length) {
          const bonus = staticBonusArr[1];
          commit(thisMapping.staticBonus, bonus);
        }

        let bonusSource = "static";

        if (value.includes("weapon attack")) {
          bonusSource = "weapon";
        } else if (value.includes("implement")) {
          bonusSource = "implement";
        } else if (value.includes("modifier")) {
          bonusSource = "statandbonus";
        }

        commit(thisMapping.bonusSource, bonusSource);
      }
    },
    parseDamage: ({ commit }, payload) => {
      const mappings = {
        primary: {
          flag: "damage/setHasDamage",
          dice: "damage/setDiceQuantity",
          sides: "damage/setDiceSides",
          staticBonus: "damage/setStaticBonus",
          statMod: "damage/setAddStatMod",
          type: "damage/setType",
        },
        secondary: {
          flag: "damage/setSecondaryHasDamage",
          dice: "damage/setSecondaryDiceQuantity",
          sides: "damage/setSecondaryDiceSides",
          staticBonus: "damage/setSecondaryStaticBonus",
          statMod: "damage/setSecondaryAddStatMod",
          type: "damage/setSecondaryType",
        },
        tertiary: {
          flag: "damage/setTertiaryHasDamage",
          dice: "damage/setTertiaryDiceQuantity",
          sides: "damage/setTertiaryDiceSides",
          staticBonus: "damage/setTertiaryStaticBonus",
          statMod: "damage/setTertiaryAddStatMod",
          type: "damage/setTertiaryType",
        },
      };

      const thisMapping =
        payload.key in mappings ? mappings[payload.key] : false;
      const value = payload.value;

      if (thisMapping && value) {
        commit(thisMapping.flag, true);

        if (!value.toLowerCase().includes("modifier")) {
          commit(thisMapping.statMod, false);
        }

        const diceArray = value.match(/(\d+)d(.*?) \+/);

        const dice = diceArray[1];
        const sides = diceArray[2];

        if (!isNaN(parseInt(dice))) {
          commit(thisMapping.dice, dice);
        }

        if (!isNaN(parseInt(sides))) {
          commit(thisMapping.sides, sides);
        }

        const staticBonusArr = value.match(/\[\[ (\d*) \]\] \[static bonus\]/);

        if (staticBonusArr && staticBonusArr.length) {
          const bonus = staticBonusArr[1];
          commit(thisMapping.staticBonus, bonus);
        }

        const damageTypeArr = value.match(/\]\] (\w*) damage\./);

        if (damageTypeArr && Array.isArray(damageTypeArr)) {
          const type = damageTypeArr[1];
          commit(thisMapping.type, type);
        }
      }
    },
  },
});
